/**
 * Hook returning recommended past conversations for a Rovo Space.
 *
 * 🚧 ROVOFE-3042 — temporary stub.
 *
 * The real implementation will call the recommendations backend (see follow-up
 * ticket) and return the user's most-relevant past conversations for the given
 * `spaceId`. For now this hook returns deterministic dummy data so the banner
 * can be visually verified end-to-end without backend wiring.
 *
 * When the real endpoint is ready, replace the body of this hook (keep the
 * signature) so consumers don't have to change.
 */
export type PastConversationRecommendation = {
	/** Conversation id from the chat backend. */
	conversationId: string;
	/** Display title of the past conversation. */
	title: string;
};

export type UsePastConversationRecommendationsResult = {
	/** Recommended past conversations for the given space. */
	recommendations: PastConversationRecommendation[];
	/** Convenience accessor — equals `recommendations.length`. */
	count: number;
	/** `true` while the recommendations are being fetched. */
	isLoading: boolean;
	/** Populated when the fetch fails. */
	error: Error | null;
};

export type UsePastConversationRecommendationsOptions = {
	/** Space the recommendations are scoped to. */
	spaceId: string;
};

/**
 * 🚧 Dummy data — the feature is not yet released, so we ship the scaffolding
 * with placeholder recommendations so internal users/teammates can see the
 * banner end-to-end. Swap this list out for a real backend call (and remove
 * this constant) in the follow-up ticket.
 */
const DUMMY_RECOMMENDATIONS: PastConversationRecommendation[] = [
	{ conversationId: 'dummy-1', title: 'Implement caching to decrease server load' },
	{ conversationId: 'dummy-2', title: 'Optimize database queries for faster reads' },
	{ conversationId: 'dummy-3', title: 'Refactor code for better readability' },
	{ conversationId: 'dummy-4', title: 'Refactor the API to reduce latency' },
	{ conversationId: 'dummy-5', title: 'Use asynchronous calls for smoother UX' },
	{ conversationId: 'dummy-6', title: 'Enhance database indexing for quick lookups' },
	{ conversationId: 'dummy-7', title: 'Memory beta spec review' },
	{ conversationId: 'dummy-8', title: 'Rovo Q4 roadmap context' },
];

export const usePastConversationRecommendations = (
	// `spaceId` is unused by the stub but kept on the signature so callers can
	// adopt the real hook later without changing call-sites.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_options: UsePastConversationRecommendationsOptions,
): UsePastConversationRecommendationsResult => {
	return {
		recommendations: DUMMY_RECOMMENDATIONS,
		count: DUMMY_RECOMMENDATIONS.length,
		isLoading: false,
		error: null,
	};
};
